/* File: src/pages/GuestPages/TicketTypeSelection.js */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreator } from '../../contexts/CreatorContext';
import api from '../../services/api';
import Button from '../../components/common/Button';
import './TicketTypeSelection.css';

/**
 * Page de sélection du type de ticket - Guinness Bright Night
 * 
 * 3 OPTIONS:
 * 1. CLASSIQUE - GRATUIT et accessible à tous (code créateur optionnel)
 * 2. GOLD - 1 500 FCFA (paiement MTN/Orange)
 * 3. VIP - 50 000 FCFA (paiement MTN/Orange, stock limité)
 * 
 * ÉVÉNEMENT: 13 Décembre 2025 à Yaoundé
 * 
 * ✅ MODIFICATION V2: Le ticket CLASSIQUE n'est plus bloqué sans code créateur
 */
const TicketTypeSelection = () => {
  const navigate = useNavigate();
  const { creatorCode, creatorName } = useCreator();
  const [vipStock, setVipStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ✅ Code créateur optionnel maintenant
  const hasCreatorRef = !!creatorCode;
  
  useEffect(() => {
    const loadVipStock = async () => {
      try {
        const response = await api.get('/tickets/vip/stock');
        const stockData = response.data?.data || response.data;
        console.log('📊 Stock VIP chargé:', stockData);
        setVipStock(stockData);
      } catch (err) {
        console.error('❌ Erreur chargement stock VIP:', err);
        setVipStock({ 
          total: 40, 
          sold: 0, 
          remaining: 40,
          percentage_sold: 0,
          is_available: true
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadVipStock();
  }, []);
  
  const handleSelectType = (ticketType) => {
    if (ticketType === 'classic') {
      // ✅ MODIFICATION: Ticket CLASSIQUE accessible avec OU sans code créateur
      navigate('/register', {
        state: {
          ticketType: 'classic',
          creatorCode: creatorCode || null  // ✅ Null si pas de code
        }
      });
    } else if (ticketType === 'gold') {
      navigate('/payment', {
        state: {
          ticketType: 'gold'
        }
      });
    } else if (ticketType === 'vip') {
      if (vipStock && vipStock.remaining <= 0) {
        setError('Désolé, tous les tickets VIP sont épuisés.');
        return;
      }
      navigate('/payment', {
        state: {
          ticketType: 'vip'
        }
      });
    }
  };
  
  const handleBack = () => {
    navigate('/event');
  };
  
  if (loading) {
    return (
      <div className="ticket-type-selection-page loading-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement des options...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="ticket-type-selection-page">
      <div className="ticket-type-container">
        <div className="back-arrow" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 19L5 12L12 5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <div className="ticket-type-logo">
          <img src="/brightnight/assets/images/logo-guinness-bright-nights.png" alt="Guinness Bright Night" />
        </div>
        
        <div className="ticket-type-header">
          <h1>Choisissez votre ticket</h1>
          <p className="event-info">Guinness Bright Night - 13 Décembre 2025 à Yaoundé</p>
        </div>
        
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}
        
        <div className="ticket-types-grid">
          
          {/* OPTION 1 : TICKET CLASSIQUE - ✅ PLUS DE BLOCAGE */}
          <div 
            className="ticket-card"
            onClick={() => handleSelectType('classic')}
          >
            <div className="ticket-card-icon">🎁</div>
            <h3 className="ticket-card-title">CLASSIQUE</h3>
            <div className="ticket-card-price ticket-card-price-free">OFFERT</div>
            <div className="ticket-card-description">
              {hasCreatorRef ? (
                <>
                  <p className="creator-invitation">✓ Invitation de <strong>{creatorName || 'créateur'}</strong></p>
                  <p>✓ Accès à l'événement</p>
                </>
              ) : (
                <>
                  <p>✓ Accès à l'événement</p>
                  <p>✓ Gratuit pour tous</p>
                </>
              )}
            </div>
            {/* ✅ SUPPRESSION DU DISABLED OVERLAY */}
          </div>
          
          {/* OPTION 2 : TICKET GOLD */}
          <div 
            className="ticket-card ticket-card-standard"
            onClick={() => handleSelectType('gold')}
          >
            <div className="ticket-card-icon">🎟️</div>
            <h3 className="ticket-card-title">GOLD</h3>
            <div className="ticket-card-price">1 500 FCFA</div>
            <div className="ticket-card-description">
              <p>✓ Accès à l'événement</p>
              <p>✓ Bon d'achat boissons (3)</p>
              <p>✓ Valable dans tous les stands</p>
            </div>
            <div className="ticket-card-payment">
              <img src="/brightnight/assets/images/mtn-logo.png" alt="MTN" className="payment-logo" />
              <img src="/brightnight/assets/images/orange-logo.png" alt="Orange" className="payment-logo" />
            </div>
          </div>
          
          {/* OPTION 3 : TICKET VIP */}
          <div 
            className={`ticket-card ticket-card-vip ${vipStock && vipStock.remaining <= 0 ? 'ticket-card-disabled' : ''}`}
            onClick={() => handleSelectType('vip')}
          >
            <div className="ticket-card-icon">👑</div>
            <h3 className="ticket-card-title">VIP</h3>
            <div className="ticket-card-price">50 000 FCFA</div>
            <div className="ticket-card-description">
              <p>✓ Accès VIP exclusif</p>
              {vipStock && (
                <div className="vip-stock">
                  {vipStock.remaining > 0 ? (
                    <p className="stock-available">
                      <span className="stock-dot"></span>
                      {vipStock.remaining} / {vipStock.total} places disponibles
                    </p>
                  ) : (
                    <p className="stock-sold-out">❌ COMPLET</p>
                  )}
                </div>
              )}
              <p>✓ Bon d'achat boissons (10)</p>
              <p>✓ 3 places assises</p>
              <p>✓ Whisky, Champagne et repas offert</p>
            </div>
            <div className="ticket-card-payment">
              <img src="/brightnight/assets/images/mtn-logo.png" alt="MTN" className="payment-logo" />
              <img src="/brightnight/assets/images/orange-logo.png" alt="Orange" className="payment-logo" />
            </div>
            {vipStock && vipStock.remaining <= 0 && (
              <div className="disabled-overlay">
                <div className="disabled-message">
                  <span className="lock-icon">🔒</span>
                  <p>Places épuisées</p>
                </div>
              </div>
            )}
          </div>
          
        </div>
        
        <div className="ticket-selection-footer">
          <p className="info-text">
            💳 Paiement sécurisé via MTN Mobile Money ou Orange Money
          </p>
          <p className="info-text">
            🎫 Ticket numérique envoyé immédiatement après confirmation
          </p>
        </div>
      </div>
    </div>
  );
};

export default TicketTypeSelection;